import {
  CheckBadgeIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  FaceSmileIcon
} from "@heroicons/react/24/outline"
import { useQueryClient } from "@tanstack/react-query"
import { LoversApi } from "api/LoversApi"
import { WithSimpleLogoHeader } from "components/WithSimpleLogoHeader"
import { Button } from "components/ui"
import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"

export const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [isVerified, setIsVerified] = useState(false)
  const [error, setError] = useState<string | null>(
    searchParams.get("code") ? null : "Verification code is missing"
  )
  const queryClient = useQueryClient()

  const handleConfirmEmail = async () => {
    const code = searchParams.get("code")

    if (!code) {
      setError("Verification code is missing")
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const res = await LoversApi().verifyEmail.verifyEmail({ verificationCode: code })
      if (res.data.verified) {
        queryClient.invalidateQueries({ queryKey: ["useUser"] })
        setIsVerified(true)
      } else {
        throw new Error("Email verification failed")
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed")
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoToLogin = () => {
    navigate("/login")
  }

  if (isLoading) {
    return (
      <WithSimpleLogoHeader>
        <div className="flex items-center justify-center">
          <div className="text-center">
            <FaceSmileIcon className="animate-spin h-12 w-12 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Verifying email...
            </h2>
          </div>
        </div>
      </WithSimpleLogoHeader>
    )
  }

  if (error) {
    return (
      <WithSimpleLogoHeader>
        <div className="flex items-center justify-center">
          <div className="text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/40">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">
              Verification Failed
            </h2>
            <p className="mt-2 text-gray-600 dark:text-gray-300">{error}</p>
            <Button onClick={handleGoToLogin} className="mt-6">
              Go to Login
            </Button>
          </div>
        </div>
      </WithSimpleLogoHeader>
    )
  }

  if (isVerified) {
    return (
      <WithSimpleLogoHeader>
        <div className="flex items-center justify-center">
          <div className="text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/40">
              <CheckBadgeIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">
              Email Verified!
            </h2>
            <p className="mt-2 text-gray-600 dark:text-gray-300">
              Your email has been successfully verified
            </p>
            <Button onClick={handleGoToLogin} className="mt-6">
              Go to Login
            </Button>
          </div>
        </div>
      </WithSimpleLogoHeader>
    )
  }

  return (
    <WithSimpleLogoHeader>
      <div className="flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/40">
            <EnvelopeIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900 dark:text-white">
            Confirm your email
          </h2>
          <p className="mt-2 text-gray-600 dark:text-gray-300">
            Click the button below to confirm your email address
          </p>
          <Button onClick={handleConfirmEmail} className="mt-6">
            Confirm email
          </Button>
        </div>
      </div>
    </WithSimpleLogoHeader>
  )
}
